import React, { useCallback, useEffect, useState } from "react";
import Pager from "../../components/Pager";
import Stat from "../../components/Stat";
import { errorMessage } from "../../services";
import { getOverview, getPlans } from "../../services/admin";
import { bulkPurchaseWebsites, getBulkPurchases } from "../../services/franchisees";
import { payPurchase } from "../../utils/razorpayCheckout";
import { dateText, money } from "../../utils/format";
import DataTable from "../../components/DataTable";
import TermsNote from "../../components/TermsNote";
import { Spinner } from "../../components/Loader";

// A Sub Admin buys website licences at the partner rate. Prices and the minimum come from the server.
export default function BulkPurchaseTab({ notify }) {
  const [plan, setPlan] = useState(null);
  const [licences, setLicences] = useState(null);
  const [qty, setQty] = useState(20);
  const [months, setMonths] = useState(1);
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [pageNumber, setPageNumber] = useState(1);
  const [error, setError] = useState("");

  const loadHistory = useCallback(async () => {
    try {
      const [{ data }, overview] = await Promise.all([getBulkPurchases({ page: pageNumber, limit: 10 }), getOverview()]);
      setOrders(data.orders);
      setMeta({ total: data.total, page: data.page, pages: data.pages });
      setLicences(overview.data.stats.licencesRemaining);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [pageNumber]);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    getPlans()
      .then(({ data }) => {
        const franchise = data.plans.find((p) => p.key === "franchise");
        setPlan(franchise);
        setQty((q) => Math.max(q, franchise ? franchise.minQuantity : 1));
      })
      .catch((err) => setError(errorMessage(err)));
  }, []);

  const min = plan ? plan.minQuantity : 1;
  const rate = plan ? plan.price : 0;
  const total = qty * rate * months;

  const submit = async () => {
    setBusy(true);
    try {
      const { data, status } = await bulkPurchaseWebsites({ quantity: qty, months });
      // 201 = confirmed and licences added; 202 = created and to be paid in Razorpay (or, without it, confirmed by Ownezzy)
      if (status === 202 && data.checkout) {
        const outcome = await payPurchase(data.checkout);
        if (outcome === "paid") notify(`Payment received: ${qty} website licences added.`);
        else if (outcome === "pending") notify("Payment received. Your licences will appear in a minute.", "warn");
        else notify("Payment not completed. Your order is saved; you can buy again any time.", "warn");
      } else {
        notify(data.message, status === 201 ? "ok" : "warn");
      }
      loadHistory();
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">FRANCHISE PARTNER</p>
          <h1>Bulk website purchase</h1>
          <p className="subheading">Buy website licences at the partner rate, then create websites for your customers.</p>
        </div>
      </div>
      {error && <div className="admin-error">{error}</div>}
      {licences !== null && <div className="stats-grid"><Stat label="Licences left" value={licences} meta="Websites you can still create" icon="▦" /></div>}

      <div className="bulk-layout">
        <div className="panel bulk-form">
          <h2>Configure your order</h2>
          <label>Number of websites <span>Minimum order: {min}</span></label>
          <div className="qty-control">
            <button onClick={() => setQty((q) => Math.max(min, q - 1))}>−</button>
            <input type="number" min={min} value={qty} onChange={(e) => setQty(Math.max(min, Math.floor(Number(e.target.value)) || min))} />
            <button onClick={() => setQty((q) => q + 1)}>＋</button>
          </div>
          <label>Subscription duration</label>
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))}>
            <option value={1}>1 month</option><option value={3}>3 months</option><option value={6}>6 months</option><option value={12}>12 months</option>
          </select>
          <div className="bulk-note">✓ New websites start on the paid plan for the months you buy.</div>
          <TermsNote />
          <button className="primary-btn full-btn" disabled={busy || !plan} onClick={submit}>{busy ? <><Spinner /> Placing order</> : "Place order →"}</button>
        </div>
        <div className="panel order-summary">
          <span className="summary-label">ORDER SUMMARY</span>
          <h2>Franchise bundle</h2>
          <div className="summary-row"><span>Websites</span><b>{qty}</b></div>
          <div className="summary-row"><span>Partner rate</span><b>{money(rate)} / month</b></div>
          <div className="summary-row"><span>Duration</span><b>{months} {months === 1 ? "month" : "months"}</b></div>
          <div className="summary-total"><span>Total due</span><strong>{money(total)}</strong></div>
          <small>Your order is confirmed once Ownezzy has received the payment. Taxes and gateway charges, if any, are added separately.</small>
        </div>
      </div>

      <div className="panel table-panel tab-gap">
        <div className="panel-heading"><div><h2>Your orders</h2><p>Licences are added when an order is confirmed.</p></div></div>
        <DataTable>
            <thead><tr><th>DATE</th><th>WEBSITES</th><th>MONTHS</th><th>AMOUNT</th><th>STATUS</th><th>LICENCES LEFT</th></tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o._id}>
                  <td>{dateText(o.createdAt)}</td><td>{o.quantity}</td><td>{o.months}</td><td>{money(o.amount)}</td>
                  <td><span className={`plan-badge badge-${o.status}`}>{o.status === "pending" ? "waiting for payment" : o.status}</span></td>
                  <td>{o.status === "paid" ? o.remaining : "—"}</td>
                </tr>
              ))}
              {!orders.length && <tr><td colSpan="6" className="empty-row">No orders yet.</td></tr>}
            </tbody>
        </DataTable>
        <Pager page={meta.page} pages={meta.pages} total={meta.total} noun="orders" onPage={setPageNumber} />
      </div>
    </>
  );
}
